<template>
  <div id="pdf-elements-modal-app" data-testid="pdf-elements-modal">
    <modal
      elem-id="modal_crud_elementos_pdf"
      size="small"
      :close-label="t('Fechar')"
      :save-label="t('Salvar')"
      :save-disabled-fn="() => !formRef?.canSave"
      :save-fn="handleSave"
      :title="modalTitle"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal"
      >

      <!-- Content only renders when modal is visible (prevents API calls on mount) -->
      <template v-if="isVisible">
        <!-- Action Buttons -->
        <div v-if="shouldShowDelete" class="delete-button-container">
          <Button
            :label="t('Excluir')"
            icon="pi pi-trash"
            severity="danger"
            data-testid="pdf-elements-delete-button"
            @click="handleDelete"
          />
        </div>

        <!-- Form -->
        <PDFElementsForm
          ref="formRef"
          :element-name="currentElementName"
          @saved="handleSaveSuccess"
          @cancel="closeModal">
        </PDFElementsForm>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { usePDFElements } from '../../composables/usePDFElements';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { log } from '../../utils/DebugLogger';
import Modal from '../../ui/Modal.vue';
import PDFElementsForm from './PDFElementsForm.vue';

interface FormInstance {
  canSave?: boolean;
  initializeForNew: () => Promise<void>;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
}

interface ModalOpenPayload {
  id?: string | null;
}

const { t } = useI18n();
const { deletePDFElement } = usePDFElements();
const { confirmAction } = useConfirmAction();

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const formRef = ref<FormInstance | null>(null);
const currentElementName = ref<string | null>(null);

const modalTitle = computed<string>(() => {
  return isEditMode.value ? t('Editar Elemento') : t('Novo Elemento');
});

const shouldShowDelete = computed<boolean>(() => {
  return isEditMode.value && !!currentElementName.value;
});

const openForNew = async (): Promise<void> => {
  log('PDFElementsModal', 'openForNew', 'Opening modal for new element');
  isVisible.value = true;
  isEditMode.value = false;
  currentElementName.value = null;

  await nextTick();

  if (formRef.value) {
    await formRef.value.initializeForNew();
  }
};

const openForEdit = async (elementName: string): Promise<void> => {
  log('PDFElementsModal', 'openForEdit', 'Opening modal for edit', elementName);
  isVisible.value = true;
  isEditMode.value = true;
  currentElementName.value = elementName;

  // Form will load data in its mounted hook
};

const closeModal = (): void => {
  log('PDFElementsModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  currentElementName.value = null;
};

const handleSave = async (): Promise<void> => {
  log('PDFElementsModal', 'handleSave', 'Saving element');
  if (formRef.value) {
    await formRef.value.handleSubmit();
  }
};

const handleSaveSuccess = (): void => {
  log('PDFElementsModal', 'handleSaveSuccess', 'Element saved successfully');
  closeModal();
  eventBus.emit('pdf-elements.refresh');
};

const handleDelete = (): void => {
  if (!currentElementName.value) return;

  const elementName = currentElementName.value;

  confirmAction({
    message: t('Tem certeza que deseja excluir este elemento?'),
    header: t('Confirmação'),
    onAccept: async () => {
      log('PDFElementsModal', 'handleDelete', 'Deleting element', elementName);

      try {
        await deletePDFElement(elementName);
        log('PDFElementsModal', 'handleDelete', 'Element deleted successfully');

        eventBus.emit('globalMessage.success', t('Elemento excluído com sucesso'));
        closeModal();
        eventBus.emit('pdf-elements.refresh');
      } catch (error) {
        log('PDFElementsModal', 'handleDelete', 'Error deleting element', error);
        eventBus.emit('globalMessage.error', t('Erro ao excluir elemento'));
      }
    }
  });
};

const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('PDFElementsModal', 'handleModalOpen', 'Received open event', data);
  if (data?.id) {
    await openForEdit(data.id);
  } else {
    await openForNew();
  }
};

const handleModalClose = (): void => {
  log('PDFElementsModal', 'handleModalClose', 'Received close event');
  closeModal();
};

onMounted(() => {
  log('PDFElementsModal', 'mounted', 'Component mounted, registering eventBus listeners');
  eventBus.on('modal.pdf-elements.open', handleModalOpen);
  eventBus.on('modal.pdf-elements.close', handleModalClose);
});

onUnmounted(() => {
  log('PDFElementsModal', 'unmounted', 'Component unmounted, removing eventBus listeners');
  eventBus.off('modal.pdf-elements.open', handleModalOpen);
  eventBus.off('modal.pdf-elements.close', handleModalClose);
});
</script>

<style scoped>
.delete-button-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
}
</style>
